import { useMemo, useState } from 'react';
import Helmet from '../lib/Helmet';
import * as HL from '../lib/heatLoad';
import { CATALOGUE } from '../data/catalogue';
import { PRICES, STOCK } from '../data/pricing';
import { SERIES } from '../data/highlights';
import { STATIONS } from '../data/indiaClimate';
import { AC_SPECS } from '../data/acSpecs';
import { roomACs, roomComparison } from '../data/roomDay';
import { pickGuideModels, usedSources } from '../data/guideInverter';
import {
  UNITS_SEO, UNITS_UPDATED, UNITS_SOURCES, SIZE_ROWS, TOOL_SIZES, TOOL_HOURS, TWO_METERS,
  sizeSpan, twoMeters, unitsContent, billText, sizeLabel,
} from '../data/guideUnits';
import { GuideHeader, GuideBody, ToolCard, Choice, Rich } from '../components/guide/GuideParts';
import TwoMeters from '../components/guide/TwoMeters';

// Route: /blog/ac-power-consumption-units-per-hour — AC guide 2, rebuilt 20
// Oct 2026 to GUIDES_PLAYBOOK.md section 3. Every word comes from
// unitsContent() in data/guideUnits.js, which scripts/guide-pages.js reads too.

function BillTool({ spans, tool }) {
  const sizes = TOOL_SIZES.map((s) => spans.find((x) => x.size === s)).filter(Boolean);
  const [size, setSize] = useState('1.5');
  const [hours, setHours] = useState(8);
  const s = sizes.find((x) => x.size === size) || sizes[0];
  return (
    <ToolCard title={tool.title}>
      <Choice label="AC size" value={s.size} onChange={setSize} options={sizes.map((x) => ({ value: x.size, label: sizeLabel(x.size) }))} />
      <Choice label="Hours a day" value={hours} onChange={setHours} options={TOOL_HOURS.map((h) => ({ value: h, label: `${h} hours` }))} />
      <p className="rounded-xl bg-gray-50 p-3.5 text-[17px] leading-snug text-gray-900" aria-live="polite"><Rich text={billText(s, hours)} /></p>
    </ToolCard>
  );
}

export default function UnitsGuide() {
  const { c, meters, spans, S } = useMemo(() => {
    const g = pickGuideModels({ catalogue: CATALOGUE, prices: PRICES, stock: STOCK, series: SERIES });
    const acs = roomACs({ catalogue: CATALOGUE, prices: PRICES, stock: STOCK, series: SERIES, specs: AC_SPECS });
    const ac = acs.three || acs.list[0];
    const m = twoMeters(TWO_METERS.map((x) => roomComparison(HL, STATIONS[x.room.city], x.room, [ac])), ac);
    const sp = SIZE_ROWS.map((s) => sizeSpan(CATALOGUE, PRICES, s)).filter(Boolean);
    const content = unitsContent(sp, g, AC_SPECS, m);
    return { c: content, meters: m, spans: sp, S: usedSources(UNITS_SOURCES, content) };
  }, []);

  return (
    <>
      <Helmet>
        <title>{UNITS_SEO.title}</title>
        <meta name="description" content={UNITS_SEO.meta} />
        <meta property="og:title" content={UNITS_SEO.title} />
        <meta property="og:description" content={UNITS_SEO.meta} />
      </Helmet>
      <main className="bg-white pb-16 pt-16 lg:pt-20">
        <article className="mx-auto max-w-[760px] px-4 text-base leading-relaxed text-gray-900">
          <GuideHeader seo={UNITS_SEO} updated={UNITS_UPDATED} crumb={UNITS_SEO.crumb} />
          <GuideBody c={c} sources={S} current="ac-power-consumption-units-per-hour" ctaSource="guide_units"
            ctaTitle="Want an AC that costs less to run?"
            watch={<TwoMeters meters={meters} watch={c.watch} sources={S} />}
            tool={<BillTool spans={spans} tool={c.tool} />} />
        </article>
      </main>
    </>
  );
}
